import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { AiPanel, type AiMessage } from "@/components/layout/AiPanel";
import { useAiAssistant } from "@/modules/marketing/ai/useAiAssistant";
import {
  dispatchByContext,
  type AiChatTurn,
} from "@/modules/marketing/ai/aiApi";

/**
 * AiAssistantPanel — `useAiAssistant` store'ni backend bilan ulagan,
 * tayyor (smart) AI panel. AppShell bir marta render qiladi.
 *
 * Vazifalari:
 *  - store.open → AiPanel'ni ochadi/yopadi (o'ng drawer, Aurora style)
 *  - store.context.autoPrompt bo'lsa, panel ochilganda avtomatik yuboradi
 *  - context.page'ga qarab to'g'ri /api/marketing/ai/* endpointni tanlaydi
 *  - loading/error holatini boshqaradi (o'zbekcha toast)
 *
 * Xabar tarixi shu komponent state'da; panel yopilganda saqlanadi (qayta
 * ochilsa davom etadi), faqat YANGI kontekst (boshqa entity/page) kelganda
 * tozalanadi.
 */
export function AiAssistantPanel() {
  const open = useAiAssistant((s) => s.open);
  const context = useAiAssistant((s) => s.context);
  const openChat = useAiAssistant((s) => s.openChat);
  const close = useAiAssistant((s) => s.close);

  const [messages, setMessages] = useState<AiMessage[]>([]);
  const [loading, setLoading] = useState(false);

  // Bitta autoPrompt'ni ikki marta yubormaslik uchun belgilash kaliti.
  const lastAutoKeyRef = useRef<string | null>(null);

  // Kontekst → chat tarixini (assistant/user) backend formatiga keltirish.
  const toHistory = useCallback(
    (list: AiMessage[]): AiChatTurn[] =>
      list.map((m) => ({ role: m.role, content: m.content })),
    [],
  );

  const runRequest = useCallback(
    async (userText: string, history: AiMessage[]) => {
      setLoading(true);
      // Streaming-siz: placeholder assistant xabari (▍) ko'rsatamiz.
      const pendingId = `a-${Date.now()}`;
      setMessages((prev) => [
        ...prev,
        { id: pendingId, role: "assistant", content: "", streaming: true },
      ]);
      try {
        const reply = await dispatchByContext(
          context,
          userText,
          toHistory(history),
        );
        setMessages((prev) =>
          prev.map((m) =>
            m.id === pendingId
              ? { ...m, content: reply, streaming: false }
              : m,
          ),
        );
      } catch {
        // Xato — placeholder'ni o'chirib, o'zbekcha toast.
        setMessages((prev) => prev.filter((m) => m.id !== pendingId));
        toast.error("AI yordamchi javob bera olmadi. Keyinroq urinib ko'ring.");
      } finally {
        setLoading(false);
      }
    },
    [context, toHistory],
  );

  // Foydalanuvchi panel inputidan yuborgan matn.
  const handleSend = useCallback(
    async (text: string) => {
      const userMsg: AiMessage = {
        id: `u-${Date.now()}`,
        role: "user",
        content: text,
      };
      // History — userMsg qo'shilishidan OLDINGI holat (backend formatiga).
      const historyBefore = messages;
      setMessages((prev) => [...prev, userMsg]);
      await runRequest(text, historyBefore);
    },
    [messages, runRequest],
  );

  // Panel ochilib, kontekstda autoPrompt bo'lsa — avtomatik yuborish.
  useEffect(() => {
    if (!open || !context) return;
    const autoPrompt = context.autoPrompt;
    if (!autoPrompt) return;

    // Kontekst kaliti — bir xil entity uchun qayta yubormaslik.
    const key = JSON.stringify({
      page: context.page,
      data: context.data,
      autoPrompt,
    });
    if (lastAutoKeyRef.current === key) return;
    lastAutoKeyRef.current = key;

    // Yangi kontekst → tarixni tozalab, autoPrompt'ni user xabari sifatida yuboramiz.
    const userMsg: AiMessage = {
      id: `u-${Date.now()}`,
      role: "user",
      content: autoPrompt,
    };
    setMessages([userMsg]);
    void runRequest(autoPrompt, []);
    // runRequest context'ga bog'liq — lekin biz aynan shu effekt ichida
    // chaqirilgan paytdagi context'ni ishlatamiz; qayta-ishga tushishni
    // faqat open/context o'zgarishi boshqaradi.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, context]);

  // Panel ochiq emas paytida store.open true bo'lsa AiPanel ochilsin.
  const handleOpenChange = useCallback(
    (next: boolean) => {
      if (next) {
        openChat(context ?? undefined);
      } else {
        close();
      }
    },
    [openChat, close, context],
  );

  return (
    <AiPanel
      open={open}
      onOpenChange={handleOpenChange}
      messages={messages}
      onSend={handleSend}
      loading={loading}
      placeholder="Marketing bo'yicha so'rang — kampaniya, kreativ, ko'rsatkichlar..."
    />
  );
}
